import { Link } from "react-router-dom";
import Nav from "../components/landing/Nav";
import { useSeo } from "../lib/seo";
import ConvictionCurve from "../components/landing/ConvictionCurve";
import FinalCta from "../components/landing/FinalCta";
import Objections from "../components/landing/Objections";
import ForNotFor from "../components/landing/ForNotFor";
import LiveThemeCurve from "../components/landing/LiveThemeCurve";
import Footer from "../components/landing/Footer";

const howLinks = [
  { href: "/", label: "← Home" },
  { href: "#curve", label: "The Curve" },
  { href: "#prints", label: "How a Print fires" },
  { href: "#receipts-explained", label: "Receipts" },
  { href: "/#pricing", label: "Pricing" },
];

const stages = [
  {
    name: "Emerging",
    color: "#8B93A3",
    body: "The first graded specialists show up in the filings. Thin evidence: the stock is on the radar, not yet a case.",
  },
  {
    name: "Early Conviction",
    color: "#2BD97C",
    body: "Several proven specialists converging on the same stock, independently, while the options tape is still quiet. This is the head start, the stage Fundprints exists to catch.",
  },
  {
    name: "Consensus",
    color: "#E8EBF0",
    body: "Participation broadens and the tape picks up. The market is catching on. Holders are being paid; fresh entries have less edge.",
  },
  {
    name: "Crowded",
    color: "#E3A44A",
    body: "On the lockstep crowding list: too many institutions in the same trade at once, or accumulation has peaked. Not a prediction, a description of who's already in. Warnings fire here.",
  },
  {
    name: "Fading",
    color: "#C75E5E",
    body: "The evidence unwinds. Specialists trim, support fades. The record shows what tends to happen next, which is why the warnings matter as much as the finds.",
  },
];

const printSteps = [
  {
    n: "01",
    k: "The evidence base recomputes",
    t: "Every week, all 10,000+ filers are re-scored, every position re-aged, and every stock re-placed on the Conviction Curve.",
  },
  {
    n: "02",
    k: "A threshold crosses",
    t: "A stock changes stage, a graded specialist joins, or the options tape turns Hot. The change itself is the event, not an opinion about it.",
  },
  {
    n: "03",
    k: "The Print fires",
    t: "Dated the moment the evidence changed, pushed to the feed, and to your alerts if it's a stock you watch. That date never moves.",
  },
];

export default function How() {
  useSeo({
    title: "How Fundprints works: Prints, the Conviction Curve, Receipts",
    description: "How we grade 10,000+ funds by their disclosed buys, follow only the proven specialists, and read every stock on the early-to-late conviction curve.",
    path: "/how",
  });
  return (
    <div data-testid="how-page" className="min-h-screen bg-[#0B0E14]">
      <Nav links={howLinks} />
      <main>
        <section className="fp-section !pt-20 !pb-14">
          <p className="fp-eyebrow mb-6">How it works</p>
          <h1
            data-testid="how-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl"
          >
            The machine, <span className="text-[#2BD97C]">explained.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#8B93A3] leading-relaxed">
            Three nouns run this product. A <span className="text-[#2BD97C]">Print</span>: the
            evidence changed. A <span className="text-[#E8EBF0]">stage</span>: where the trade
            sits on the Conviction Curve. A <span className="text-[#E3A44A]">Receipt</span>: the
            timestamped proof of what happened between Prints. Here's the whole mechanism.
          </p>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-[#8B93A3] leading-relaxed">
            And it isn't only stocks. Most of what you own, you own through funds. We read those
            too, holding by holding, including the target-date fund in your{" "}
            <Link to="/how-crowded-is-my-401k" className="text-[#2BD97C] hover:underline">401k</Link>.
          </p>
        </section>

        <ConvictionCurve />

        <section id="stages" className="fp-section">
          <p className="fp-eyebrow mb-6">The five stages</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
            Where a stock sits tells you what the evidence is worth.
          </h2>
          <div className="mt-12 space-y-0">
            {stages.map((s) => (
              <div
                key={s.name}
                data-testid={`stage-row-${s.name.replace(" ", "-")}`}
                className="grid md:grid-cols-[220px_1fr] gap-2 md:gap-8 py-5 border-b border-[rgba(255,255,255,0.06)]"
              >
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="font-mono text-sm font-semibold" style={{ color: s.color }}>
                    {s.name}
                  </span>
                </div>
                <p className="text-sm text-[#8B93A3] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="prints" className="border-y border-[rgba(255,255,255,0.06)] bg-[#0D111A]">
          <div className="fp-section">
            <p className="fp-eyebrow mb-6">How a Print fires</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
              A Print is not a tip. It's a dated change in the evidence.
            </h2>
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {printSteps.map((b) => (
                <div key={b.n} data-testid={`print-step-${b.n}`} className="fp-card p-7">
                  <div className="font-mono text-xs text-[#2BD97C] mb-4">{b.n}</div>
                  <h3 className="text-lg font-semibold mb-3">{b.k}</h3>
                  <p className="text-sm text-[#8B93A3] leading-relaxed">{b.t}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="receipts-explained" className="fp-section">
          <p className="fp-eyebrow mb-6">What a Receipt proves</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
            Every claim on this site has a date attached.
          </h2>
          <p className="mt-5 max-w-2xl text-base text-[#8B93A3] leading-relaxed">
            When a journey completes (say, a stock enters Early Conviction and later turns
            Crowded), the pair of Prints becomes a Receipt: the entry evidence, the exit evidence,
            the move between them, and what the S&amp;P 500 did over the same window. Receipts are
            generated from the record as it stood, dated when it happened. The warnings get
            Receipts too. <Link to="/#receipts" className="text-[#2BD97C] hover:underline">check the archive</Link>.
          </p>
        </section>

        <LiveThemeCurve />
        <Objections />
        <ForNotFor />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
